/* eslint-disable react/no-unused-prop-types */
import { ReactNode, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Field } from 'react-final-form';
import { Button, Modal, RenderIf } from 'react-rainbow-components';
import { Ban } from 'lucide-react';
import { useReduxForm } from '@rainbow-modules/hooks';
import { showAppNotification } from '@rainbow-modules/app';
import UniversalForm from 'components/UniversalForm';
import ModalBody from 'components/ModalBody';
import MicrophoneIcon from 'components/icons/microphone';
import DictateForm from 'components/SendNoteForm/DictateForm';
import useMultipartMutation from 'data/hooks/useMultipartMutation';
import {
    ActionButton,
    ActionsContainer,
    ButtonsContainer,
    DictateFormContainer,
    FieldContainer,
    FieldLabel,
    InputWrapper,
    ModalContent,
    ReasonField,
    RequiredMark,
} from './styled';

const FORM_ID = 'void-invoice-form';

const buttonStyles = {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    marginLeft: '0.7rem',
};

interface VoidReasonInputProps {
    id?: string;
    className?: string;
    name?: string;
    label?: ReactNode;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}

const VoidReasonInput = (props: VoidReasonInputProps) => {
    const { agentId = '' } = useParams();
    const {
        value: valueInProps = '',
        onChange = () => {},
        error: errorInProps,
        label: labelInProps,
        placeholder: placeholderInProps,
        required: requiredInProps,
        disabled: disabledInProps,
        id: idInProps,
        className: classNameInProps,
        name: nameInProps,
    } = useReduxForm(props);
    const [isDictating, setIsDictating] = useState(false);
    const {
        isLoading: isTranscribing,
        mutateAsync: transcribeAudio,
    } = useMultipartMutation<FormData, { transcription?: string }>({
        method: 'POST',
        pathname: `/agents/${agentId}/transcribe`,
    });
    const disableElements = isTranscribing || disabledInProps;

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange(event.target.value);
        },
        [onChange],
    );

    const handleDictateAccept = useCallback(async (blob: Blob | null) => {
        setIsDictating(false);
        if (!blob) return;

        try {
            const formData = new FormData();
            formData.append('audio', blob);
            const { transcription } = await transcribeAudio({
                body: formData,
            });
            if (transcription) {
                onChange(transcription);
            }
        } catch (err) {
            const message = (err as Error)?.message || 'Something went wrong. Please try again.';
            showAppNotification({
                title: 'Error',
                description: message,
                icon: 'error',
                timeout: 5000,
            });
        }
    }, [onChange, transcribeAudio]);

    if (isDictating) {
        return (
            <FieldContainer className={classNameInProps}>
                <RenderIf isTrue={Boolean(labelInProps)}>
                    <FieldLabel>
                        {labelInProps}
                        <RenderIf isTrue={requiredInProps}>
                            <RequiredMark title="required">*</RequiredMark>
                        </RenderIf>
                    </FieldLabel>
                </RenderIf>
                <DictateFormContainer>
                    <DictateForm
                        onAccept={handleDictateAccept}
                        onCancel={() => setIsDictating(false)}
                    />
                </DictateFormContainer>
            </FieldContainer>
        );
    }

    return (
        <FieldContainer className={classNameInProps}>
            <RenderIf isTrue={Boolean(labelInProps)}>
                <FieldLabel>
                    {labelInProps}
                    <RenderIf isTrue={requiredInProps}>
                        <RequiredMark title="required">*</RequiredMark>
                    </RenderIf>
                </FieldLabel>
            </RenderIf>
            <InputWrapper>
                <ReasonField
                    id={idInProps}
                    name={nameInProps}
                    placeholder={placeholderInProps}
                    disabled={disableElements}
                    required={requiredInProps}
                    error={errorInProps}
                    value={valueInProps}
                    onChange={handleChange}
                    rows={3}
                />
                <ActionsContainer>
                    <ActionButton
                        tooltip="Dictate"
                        icon={<MicrophoneIcon />}
                        onClick={() => setIsDictating(true)}
                        disabled={disableElements}
                    />
                </ActionsContainer>
            </InputWrapper>
        </FieldContainer>
    );
};

interface VoidInvoiceModalProps {
    isOpen?: boolean;
    invoiceNumber?: string;
    isSubmitting?: boolean;
    onRequestClose: () => void;
    onConfirm: (note: string) => void;
}

const VoidInvoiceModal = ({
    isOpen,
    invoiceNumber,
    isSubmitting,
    onRequestClose,
    onConfirm,
}: VoidInvoiceModalProps) => {
    const description = invoiceNumber
        ? `Void ${invoiceNumber}? This cannot be undone. The invoice will stay visible but can no longer be marked as sent or paid.`
        : 'This cannot be undone. The invoice will stay visible but can no longer be marked as sent or paid.';

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            title=""
            footer={(
                <ButtonsContainer>
                    <Button
                        // eslint-disable-next-line react/forbid-component-props
                        style={buttonStyles}
                        label="Cancel"
                        variant="border"
                        borderRadius="semi-square"
                        disabled={isSubmitting}
                        onClick={onRequestClose}
                    />
                    <Button
                        // eslint-disable-next-line react/forbid-component-props
                        style={buttonStyles}
                        label="Void"
                        variant="destructive"
                        type="submit"
                        form={FORM_ID}
                        borderRadius="semi-square"
                        disabled={isSubmitting}
                    />
                </ButtonsContainer>
            )}
        >
            <UniversalForm
                id={FORM_ID}
                key={isOpen ? 'open' : 'closed'}
                onSubmit={(values: Record<string, unknown>) => {
                    onConfirm(String(values.note || '').trim());
                }}
            >
                <ModalContent>
                    <ModalBody
                        variant="destructive"
                        icon={<Ban />}
                        title="Void invoice?"
                        description={description}
                    />
                    <Field
                        name="note"
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={VoidReasonInput}
                        label="Void Reason"
                        placeholder="Why is this invoice being voided?"
                        required
                        disabled={isSubmitting}
                        validate={(value: unknown) => (
                            String(value || '').trim()
                                ? undefined
                                : 'Void reason is required.'
                        )}
                    />
                </ModalContent>
            </UniversalForm>
        </Modal>
    );
};

export default VoidInvoiceModal;
